import { useEffect, useState } from 'react'
import type { Approche, ApprocheDTO, AdversaireDTO, CombatDTO, PersonnageDTO } from '@whatremains/shared'
import { api, ErreurApi, type RegionAdversaires } from '../api'

/**
 * Choisir qui, puis comment. **Deux pages, jamais une seule.**
 *
 * ## Ce qui n'allait pas
 *
 * Reproche d'Enzo, en jouant : « pourquoi est-ce que toute la liste ne
 * disparaît pas ? Pourquoi est-ce que je suis obligé d'aller tout en bas de
 * tout swiper, surtout sur téléphone ? C'est vraiment très long. »
 *
 * L'écran empilait tout : les sept régions et leurs quarante-quatre
 * adversaires, puis, **en dessous**, le choix de l'approche qui n'apparaissait
 * qu'une fois la cible prise. Choisir un adversaire du haut de la liste
 * obligeait donc à traverser toute la liste pour trouver le geste suivant, à
 * chaque combat.
 *
 * Le défaut n'était pas la longueur de la liste, c'était d'avoir mis deux
 * questions successives sur le même plan. On répond à la première, la liste
 * s'efface, et la seconde arrive seule avec ce qu'il faut pour y répondre.
 *
 * ## Ce que la seconde page montre
 *
 * Ce qu'on va y gagner, et **où l'on se situe** face à lui. C'est la donnée qui
 * décide, et elle n'était nulle part : la liste donnait le niveau de
 * l'adversaire sans jamais le rapprocher du sien. Depuis que la fuite est
 * plafonnée à une chance sur deux, engager quelqu'un de trop fort est une
 * erreur qui se paie, donc l'écart doit se lire avant d'engager, pas après.
 *
 * Et un retour, évidemment : « il faut que j'aie un bouton retour si j'ai pas
 * envie d'attaquer celui-là et que je me suis trompé ».
 *
 * L'approche, elle, reste posée **avant** l'engagement, et c'est le point qui
 * n'a pas changé : elle module le combat qu'on joue, et elle devient la
 * consigne du pilote quand on choisit de ne pas le jouer.
 */

/** L'écart de niveau, dit en clair plutôt qu'en chiffre nu. */
function ecartDit(adversaire: number, moi: number): { texte: string; ton: string } {
  const d = adversaire - moi
  if (d <= -3) return { texte: 'Bien en dessous de toi.', ton: 'facile' }
  if (d < 0) return { texte: 'En dessous de toi.', ton: 'facile' }
  if (d === 0) return { texte: 'À ton niveau exactement.', ton: 'egal' }
  if (d <= 2) return { texte: `${d} niveau${d > 1 ? 'x' : ''} au-dessus de toi.`, ton: 'dur' }
  return { texte: `${d} niveaux au-dessus de toi. Ça peut très mal se passer.`, ton: 'perilleux' }
}

export function Engager({
  personnage,
  surEngage,
  surSortie,
  cibleImposee,
}: {
  personnage: PersonnageDTO
  surEngage: (c: CombatDTO) => void
  surSortie: () => void
  /**
   * L'adversaire imposé par une étape de quête, s'il y en a un.
   *
   * Enzo : « au lieu de me dire d'aller moi-même chercher mon ennemi, que les
   * ennemis me soient directement présentés quand je clique sur se battre. Aller
   * le chercher, le battre, le rechercher, le rebattre, puis retourner dans les
   * quêtes, ça n'a pas de sens. »
   *
   * On ouvre donc directement la SECONDE page, celle qui montre ce qu'il
   * rapporte, où l'on se situe face à lui et l'approche à poser. C'est le même
   * écran de combat, à la liste près — qu'on saute, puisque le choix est fait.
   *
   * Et le retour ne renvoie plus à la liste, il renvoie à la quête : personne
   * n'a demandé le bestiaire, on y était déjà passé.
   */
  cibleImposee?: string
}) {
  const [regions, setRegions] = useState<RegionAdversaires[] | null>(null)
  const [toutMontrer, setToutMontrer] = useState(false)
  const [approches, setApproches] = useState<ApprocheDTO[]>([])
  const [cible, setCible] = useState<AdversaireDTO | null>(null)
  const [approche, setApproche] = useState<Approche>('directe')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    void Promise.all([api.bestiaire(personnage.id), api.approches()]).then(([b, a]) => {
      setRegions(b)
      setApproches(a)
      // La cible imposée par une quête ouvre directement la page de
      // confirmation. On la cherche par CODE et jamais par nom : deux
      // adversaires peuvent porter le même.
      if (cibleImposee) {
        for (const r of b) {
          const trouve = r.adversaires.find((x) => x.code === cibleImposee)
          if (trouve) { setCible(trouve); break }
        }
      }
    })
  }, [cibleImposee, personnage.id])

  // On repart du haut en changeant de page. Sans ça, choisir un adversaire du
  // bas de la liste ouvrirait la page suivante à mi-hauteur, ce qui est
  // exactement le désagrément qu'on vient de retirer.
  useEffect(() => { window.scrollTo({ top: 0 }) }, [cible])

  if (!regions) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  /**
   * Ce qui vaut encore la peine d'être affronté.
   *
   * Dix niveaux sous soi : le même seuil que pour replier une région de
   * besogne, et pour la même raison — passé cet écart, l'adversaire ne rapporte
   * plus rien et ne présente plus aucun risque. Ce n'est pas un jugement sur sa
   * difficulté, c'est un constat sur ce que le combat produit.
   */
  const interessant = (a: { niveau: number }) => a.niveau >= personnage.niveau - 10
  const regionsVues = toutMontrer
    ? regions
    : regions.filter((r) => r.adversaires.some(interessant))
  const caches = regions.reduce(
    (n, r) => n + r.adversaires.filter((a) => !interessant(a)).length, 0,
  )
  // Nommées, pas comptées : « Les Marches basses et Les Carrières » se vérifie
  // d'un coup d'œil, « deux régions » demande de croire sur parole.
  const regionsRepliees = regions
    .filter((r) => !r.adversaires.some(interessant))
    .map((r) => r.nom)

  async function engager(interactif: boolean) {
    if (!cible) return
    setErreur(null)
    setEnCours(true)
    try {
      surEngage(await api.engager(personnage.id, cible.code, approche, interactif))
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    } finally {
      setEnCours(false)
    }
  }

  // ─── La seconde page : celui-là, et comment ──────────────────────────────
  if (cible) {
    const ecart = ecartDit(cible.niveau, personnage.niveau)
    const partVie = Math.round((personnage.pv / Math.max(1, personnage.statsDerivees.pvMax)) * 100)
    return (
      <div className="cadre">
        <div className="entete">
          <button className="discret" onClick={() => (cibleImposee ? surSortie() : setCible(null))}>
            {cibleImposee ? '← Revenir à la quête' : '← Choisir quelqu’un d’autre'}
          </button>
          {/*
            Le niveau auquel on l'affrontera, et d'ou il vient.

            Enzo : « je tombe sur ces ennemis classes bien en dessous de toi et
            pourtant je me fais arracher ». Sa quete, ecrite pour le niveau 6 et
            acceptee au 11, se joue au 10 : le contremaitre de niveau 7 est donc
            affronte en version niveau 10. L'ecran montrait 7.
          */}
          <div className="surtitre" style={{ marginTop: 14 }}>
            Niveau {cible.niveau}
            {cible.niveauEcrit !== null && (
              <span style={{ color: 'var(--r7)' }}>
                {' '}· monte a ton niveau par ta quete, ecrit pour le {cible.niveauEcrit}
              </span>
            )}
          </div>
          <h1>{cible.nom}</h1>
          <p className="attenue" style={{ maxWidth: '58ch' }}>{cible.texte}</p>

          <div className="avant-combat">
            <div>
              <span className="etiquette">Ce que ça rapporte</span>
              <b className="chiffre-fort">{cible.xpGagne} xp</b>
              <b className="chiffre-fort">{cible.orGagne} or</b>
            </div>
            <div>
              <span className="etiquette">Où tu te situes</span>
              <b className={`ecart ecart-${ecart.ton}`}>{ecart.texte}</b>
              {/*
                Et ce qu'il vaut PARMI CEUX DE SON NIVEAU.

                L'écart de niveau seul ment sur les élites : au niveau 20,
                « Éclaireur de Tenaille » a 611 points de vie et « Le premier
                relevé » 907, et cette ligne disait exactement la même chose des
                deux. Quatre adversaires sur quarante-cinq sont redoutables,
                quatre coriaces ; les trente-sept autres ne disent rien, sans
                quoi la mention deviendrait du bruit.
              */}
              {cible.stature !== 'ordinaire' && (
                <b className={`ecart ecart-${cible.stature === 'redoutable' ? 'perilleux' : 'dur'}`}>
                  {cible.stature === 'redoutable'
                    ? 'Bien plus fort que les autres de son niveau.'
                    : 'Plus coriace que les autres de son niveau.'}
                </b>
              )}
            </div>
            {/*
              Dans quel état tu pars.

              L'écran disait ce que l'adversaire rapporte et où l'on se situe
              face à lui, jamais combien de points de vie il nous reste. Enzo a
              enchaîné quatre combats en une demi-heure en repartant chaque fois
              d'un fond de barre : la régénération rend un centième du total par
              minute, donc cent minutes pour se remettre à plein. Engager à
              treize pour cent de sa vie est une décision — encore faut-il
              savoir qu'on la prend.
            */}
            <div>
              <span className="etiquette">Dans quel état tu pars</span>
              <b className={partVie < 40 ? 'ecart ecart-perilleux' : 'chiffre-fort'}>
                {personnage.pv} / {personnage.statsDerivees.pvMax}
                {partVie < 40 && <> · tu n’es pas remis</>}
              </b>
            </div>
          </div>
        </div>

        <section>
          <div className="surtitre">Comment tu y vas</div>
          <div className="grille" style={{ maxWidth: 900 }}>
            {approches.map((a) => (
              <button
                key={a.code}
                className="choix"
                aria-pressed={approche === a.code}
                onClick={() => setApproche(a.code)}
              >
                <span className="nom">{a.nom}</span>
                <p style={{ marginTop: 8 }}>{a.texte}</p>
              </button>
            ))}
          </div>

          {erreur && <p className="erreur">{erreur}</p>}

          <p style={{ marginTop: 26, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="action" disabled={enCours} onClick={() => void engager(true)}>
              Engager
            </button>
            <button className="discret" disabled={enCours} onClick={() => void engager(false)}>
              Envoyer sans regarder
            </button>
          </p>
          <p className="tenu" style={{ fontSize: 12, maxWidth: '52ch' }}>
            Sans regarder, le combat se résout d’un trait en suivant l’approche que tu viens de
            poser. C’est ce que feront les quêtes minutées.
          </p>
        </section>

        <section>
          <button className="discret" onClick={() => (cibleImposee ? surSortie() : setCible(null))}>
            {cibleImposee ? 'Revenir à la quête' : 'Revenir à la liste'}
          </button>
        </section>
      </div>
    )
  }

  // ─── La première page : qui ──────────────────────────────────────────────
  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {personnage.nom} · niveau {personnage.niveau} · {personnage.pv} / {personnage.statsDerivees.pvMax}
        </div>
        <h1>Qui</h1>
        <p className="attenue" style={{ maxWidth: '54ch' }}>
          Choisis quelqu’un. Tu verras ensuite ce qu’il rapporte, et comment tu veux y aller.
        </p>
      </div>

      {/*
        Les adversaires qu'on ne réaffrontera jamais se replient.

        Enzo : « dans la partie Se battre on a aussi le souci d'avoir plein
        d'ennemis bas niveau que je ne reverrai jamais puisque trop bas, et je
        dois quand même tout scroller pour arriver aux ennemis qui
        m'intéressent. »

        Même défaut que les régions de besogne, même remède, et le même seuil de
        dix niveaux — un adversaire dix niveaux sous soi ne rapporte plus rien et
        ne risque plus rien. On replie, on ne supprime pas : il reste
        atteignable d'un clic, parce qu'une quête peut très bien en nommer un.

        Une région entièrement repliée disparaît de la liste avec ses
        adversaires : garder son titre au-dessus du vide serait remplacer un
        défilement par un autre.
      */}
      {/*
        L'avis de repli est en TÊTE, et c'est une correction.

        Enzo, après coup : « ni sur mobile ni sur desktop je ne les vois repliés
        dans la section Se battre. » Le repli marchait pourtant — vérifié sur la
        vraie réponse de l'API, vingt et un adversaires et trois régions
        entières disparaissent à son niveau. Ce qui manquait, c'était de le
        DIRE : le seul indice était un bouton `discret` posé tout en bas, après
        la liste complète, c'est-à-dire à l'endroit exact où l'on ne va que si
        l'on a déjà lu tout le reste.

        Une absence s'explique là où elle se produit, pas après. Et on nomme les
        régions retirées : « trois régions et vingt et un adversaires » se
        vérifie d'un coup d'œil, « des adversaires trop bas » demande de croire
        sur parole.
      */}
      {caches > 0 && (
        <section>
          <p className="repli-avis">
            {toutMontrer ? (
              <>Tout est affiché, y compris ce qui ne te rapporte plus rien.</>
            ) : (
              <>
                <b>{caches} adversaires</b> sont plus de dix niveaux sous toi et ne
                t’apportent plus ni or, ni expérience, ni risque
                {regionsRepliees.length > 0 && (
                  <> — dont {regionsRepliees.join(', ')}, repliées en entier</>
                )}
                .
              </>
            )}{' '}
            <button className="lien" onClick={() => setToutMontrer((v) => !v)}>
              {toutMontrer ? 'les replier' : 'tout afficher quand même'}
            </button>
          </p>
        </section>
      )}

      {regionsVues.map((r) => (
        <section key={r.code}>
          <div className="surtitre">
            {r.nom} · à partir du niveau {r.seuilNiveau}
          </div>
          <div className="grille">
            {(toutMontrer ? r.adversaires : r.adversaires.filter(interessant)).map((a) => {
              const d = a.niveau - personnage.niveau
              return (
                <button key={a.code} className="choix" onClick={() => setCible(a)}>
                  <span className="role">
                    niveau {a.niveau}
                    {/* Une élite se repère dès la liste, sinon on la choisit
                        justement parce qu'elle avait l'air comme les autres. */}
                    {a.stature !== 'ordinaire' && (
                      <span style={{ color: a.stature === 'redoutable' ? 'var(--r6)' : 'var(--r7)' }}>
                        {' '}· {a.stature}
                      </span>
                    )}
                  </span>
                  <span className="nom">{a.nom}</span>
                  <p style={{ marginTop: 8 }}>{a.texte}</p>
                  <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 11 }}>
                    {a.orGagne} or · {a.xpGagne} xp
                    {/*
                      L'écart de niveau se lit dès la liste, en couleur. C'est ce
                      qui permet de balayer sans lire : on cherche la teinte qu'on
                      veut, au lieu de soustraire son niveau de chaque ligne.
                    */}
                    <span
                      className={`ecart ecart-${ecartDit(a.niveau, personnage.niveau).ton}`}
                      style={{ marginLeft: 10 }}
                    >
                      {d > 0 ? `+${d}` : d}
                    </span>
                  </p>
                </button>
              )
            })}
          </div>
        </section>
      ))}

      <section>
        <button className="discret" onClick={surSortie}>Revenir</button>
      </section>
    </div>
  )
}
